import { config } from '../../../config';
import { shortId } from '../../lib/ids';
import { useLayout } from '../../providers/LayoutProvider';
import PageMeta from '../PageMeta';
import type { Crumb } from './crumbs';
import React from 'react';
import { useLocation } from 'react-router';

type Props = React.PropsWithChildren<{
  title?: string;
  /**
   * Popis stránky do `<meta name="description">` a Open Graph. Bez neho platí
   * všeobecný popis aplikácie ([[PageMeta]]). Detail, ktorý má vlastné dáta,
   * ho prebije vlastným `PageMeta`.
   */
  description?: string;
  backPath?: string;
  /**
   * Stránka je koreň sekcie - chodí sa na ňu z navigácie, takže hlavička na nej
   * nekreslí šípku Späť. Označujú sa **len** korene: šípka je v shelli zapnutá
   * všade a cieľ si dopočíta z `crumbs`, keď route nedá `backPath`.
   */
  root?: boolean;
  withoutLayoutPadding?: boolean;
  /**
   * Stránka si hlavičku (cestu, titul, akcie) kreslí **sama** - shell ju
   * nekreslí, hoci route dodá titul. Titul ide ďalej do `<title>` dokumentu.
   *
   * Je to výnimka pre jedinú obrazovku: profil má vedľa hlavičky ľavý pás so
   * sekciami, a shellová hlavička stojí nad celou plochou stránky, teda aj nad
   * pásom. Nikde inde to nepotrebuj - `PageHeader` patrí shellu
   * ([shell.md](../../../.docs/vyvoj/shell.md)).
   */
  withoutPageHeader?: boolean;
  /**
   * Celá cesta vrátane „Domov". Bez nej sa cesta odvodí z URL, čo dá články
   * v podobe segmentov (`Kategorie-podnetov`) - použiteľné pre plytké verejné
   * routy, nie pre administráciu. Tá má hotové cesty v
   * [crumbs.ts](crumbs.ts) a musí to byť **stabilná referencia**, inak sa
   * efekt nižšie spúšťa pri každom renderi.
   */
  crumbs?: Crumb[];
}>;

/**
 * Neverejné časti appky. Do vyhľadávača nepatria - za prihlásením by robot
 * aj tak videl len prázdnu stránku, a zaindexovaná adresa administrácie je
 * len pozvánka skúšať ju.
 */
const NO_INDEX_PREFIXES = ['/administracia', '/profil'];

/**
 * Článok cesty odvodenej z URL. Odkazy z notifikácií nesú v adrese celé URN
 * (`/novinky/urn:ngsi-ld:presov.sk:digital-obcan:spr:FX77H5X`), a kým sa
 * segment len kapitalizoval, cesta ukazovala `Urn:ngsi-ld:…` (moj-presov#34).
 * Z URN preto ostane holý koncový segment, rovnako ako v adrese, ktorú tvorí
 * appka sama ([ids.ts](../../lib/ids.ts)); segment bez dvojbodky sa nemení.
 */
const crumbLabel = (part: string): string => {
  const label = shortId(part);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const RouteMetadata: React.FC<Props> = ({
  title,
  description,
  backPath,
  root = false,
  withoutLayoutPadding = false,
  withoutPageHeader = false,
  crumbs,
  children,
}) => {
  const { pathname } = useLocation();
  const {
    setPageTitle,
    setBackPath,
    setIsRootPage,
    setActions,
    setWithoutLayoutPadding,
    setTabs,
    setSearchBarParams,
    setIsLoadingTabs,
    setTabQueryParamName,
    setBreadcrumbs,
    setSearchValue,
    setFooter,
  } = useLayout();

  React.useLayoutEffect(() => {
    if (crumbs) {
      setBreadcrumbs(crumbs);
    } else {
      const pathParts = pathname.split('/').filter(Boolean);
      setBreadcrumbs([
        {
          href: '/',
          label: 'Domov',
        },
        ...pathParts.map((part, index) => ({
          href: `/${pathParts.slice(0, index + 1).join('/')}`,
          label: crumbLabel(part),
        })),
      ]);
    }
    if (title && !withoutPageHeader) {
      setPageTitle(title);
    }

    if (backPath) {
      setBackPath(backPath);
    }

    setIsRootPage(root);

    if (withoutLayoutPadding) {
      setWithoutLayoutPadding(withoutLayoutPadding);
    }

    return () => {
      setSearchValue('');
      setTabs([]);
      setFooter(null);
      setPageTitle(undefined);
      setBackPath(undefined);
      setIsRootPage(false);
      setActions([]);
      setTabs([]);
      setIsLoadingTabs?.(false);
      setTabQueryParamName?.(config.DEFAULT_TAB_QUERY_PARAM_NAME);
      setWithoutLayoutPadding(false);
      setSearchBarParams(undefined);
    };
  }, [
    backPath,
    root,
    setBackPath,
    setIsRootPage,
    setPageTitle,
    setActions,
    setSearchBarParams,
    setTabs,
    title,
    withoutLayoutPadding,
    withoutPageHeader,
    setWithoutLayoutPadding,
    setIsLoadingTabs,
    setTabQueryParamName,
    pathname,
    crumbs,
    setBreadcrumbs,
    setSearchValue,
    setFooter,
  ]);

  return (
    <>
      <PageMeta
        title={title}
        description={description}
        noIndex={NO_INDEX_PREFIXES.some((prefix) =>
          pathname.startsWith(prefix),
        )}
      />
      {children}
    </>
  );
};

export default RouteMetadata;
